import InitialFetchNoticeDialog from './InitialFetchNoticeDialog';
import useAgentsHome from './useAgentsHome';
import { useCallback, useState, useEffect } from 'react';
import ProcessSelectionDialog from './ProcessSelectionDialog';
import CreateFunnelDialog from './CreateFunnelDialog';
import useSweepNavigate from '../../navigation-utils/useSweepNavigate';
import { appRoutes } from '../../constants/appRoutes';
import { useLocation } from 'react-router';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import { useFunnelMaps } from '../pages/funnel-maps/useFunnelMaps';
import { useDispatch, useSelector } from 'react-redux';
import { selectFunnelMapsIsLoading, selectFunnelMapsList } from '../../reducers/funnelMapsReducer';
import { selectDefaultCreationCrmOrgId, selectUserAccountId } from '../../reducers/userInfoReducer';
import ConnectToSfDialog from '../pages/get-started/connect-org/ConnectToSfDialog';
import { Permission } from '@server/permissions';
import { useUnhappyStates } from '../pages/environments/useUnhappyStates';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../services/events';
import { AgentType } from '@server/ai';
import useAgentsChats from './useAgentsChats';
import { selectActiveAgentId, selectActiveChatId } from './agentsChatsReducer';
import useAiAgents from './useAiAgents';
import AgentsHomeContent from './AgentsHomeContent';
import { ArtifactProvider, useArtifacts } from '../common/artifacts/useArtifacts';
import { TableArtifactProvider } from '../common/artifacts/useTableArtifacts';
import { CreateMonitoringAgentDialogWrapper } from './CreateMonitoringAgentDialogWrapper';
import { SidebarCollapseProvider, useSidebarCollapse } from './agentSideMenu/useSidebarCollapse';
import { selectPendingArtifactToOpen, clearPendingArtifactToOpen } from './agentsHomeReducer';
import { useAgentChatInstanceOptional } from './AgentChatInstanceContext';
import { ChatInstance } from './ChatInstance';
import { useHasAllPermissions } from '../common/permissions/useHasAllPermissions';

interface AgentsHomeWithDialogsProps {
  closePanel?: () => void;
  isPageView?: boolean;
}

const AgentsHomeWithDialogs = ({ closePanel, isPageView }: AgentsHomeWithDialogsProps) => {
  const dispatch = useDispatch();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { isInitialFetch, noConnectedOrg } = useUnhappyStates();
  const [isProcessSelectionDialogOpened, setIsProcessSelectionDialogOpened] = useState(false);
  const [isNewMonitoringAgentDialogOpened, setIsNewMonitoringAgentDialogOpened] = useState(false);

  const [isCreateFunnelDialogOpened, setIsCreateFunnelDialogOpened] = useState(false);
  const [isConnectSfDialogOpen, setIsConnectSfDialogOpen] = useState(false);
  const { setIsAgentCreateActive } = useAgentsHome();
  const { fetchChats } = useAgentsChats();
  const { fetchAiAgents, fetchAgentIssues } = useAiAgents();
  const { navigate } = useSweepNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { fetchFunnelMaps } = useFunnelMaps();
  const funnelMaps = useSelector(selectFunnelMapsList);
  const isLoadingFunnelMaps = useSelector(selectFunnelMapsIsLoading);
  const connectSfPermissionStr: Permission[] = ['create:orgs:crm'];
  const isAllowedConnectSf = useHasAllPermissions(connectSfPermissionStr);
  const { openConfirm, onCancel } = useConfirm();
  const sendBiEvent = useSendBiEvent();
  const activeAgentId = useSelector(selectActiveAgentId);
  const accountId = useSelector(selectUserAccountId);

  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const pendingArtifact = useSelector(selectPendingArtifactToOpen);
  const artifactsContext = useArtifacts();

  useRunOnceWhenTruthy(
    () => {
      fetchFunnelMaps({ includeFunnelsData: true });
    },
    !isLoadingFunnelMaps && funnelMaps.length === 0 && !!crmOrgId,
  );

  //refetch agents every time the agents cards are rendered, to get the most up-to-date "facts" per agent
  useEffect(() => {
    if (activeAgentId) {
      fetchChats();
      fetchAgentIssues(activeAgentId);
    } else {
      fetchAiAgents();
    }
  }, [fetchChats, fetchAiAgents, activeAgentId, fetchAgentIssues]);

  // Handle pending artifact from artifacts page navigation
  // Wait until the chat has synced to the target chat before opening the panel
  useEffect(() => {
    if (!pendingArtifact) {
      return;
    }

    // Wait until the active chat matches the target chat
    // This prevents opening the panel while on GHOST_CHAT_ID
    if (pendingArtifact.targetChatId !== activeChatId) {
      return;
    }

    // Add artifact data to context and open the panel
    artifactsContext.addArtifactData(pendingArtifact.artifactId, pendingArtifact.artifactData);
    artifactsContext.openExistingArtifact(pendingArtifact.artifactId, pendingArtifact.versionId);

    // Clear the pending artifact from Redux
    dispatch(clearPendingArtifactToOpen());
  }, [pendingArtifact, artifactsContext, dispatch, activeChatId]);

  const navigateAndActivate = useCallback(
    (funnelMapId: string) => {
      sendBiEvent({
        name: AI_AGENTS_EVENTS.newAgentClick,
        props: { origin: 'agent_card', app: 'dashboard' },
      });
      setIsAgentCreateActive(true);
      navigate(`${appRoutes.funnelEditor.route}/${funnelMapId}/${accountId}`);
    },
    [navigate, sendBiEvent, setIsAgentCreateActive, accountId],
  );

  const handleNoOrgFirst = useCallback(
    async (cb: () => void) => {
      if (noConnectedOrg) {
        if (isAllowedConnectSf) {
          setIsConnectSfDialogOpen(true);
        }
        return;
      }
      if (isInitialFetch) {
        await openConfirm(<InitialFetchNoticeDialog closeDialog={onCancel} />);
        return;
      }
      cb();
    },
    [isAllowedConnectSf, isInitialFetch, noConnectedOrg, onCancel, openConfirm],
  );

  const handleNewProcessAgent = useCallback(async () => {
    const isInCanvas = pathname.startsWith(appRoutes.funnelEditor.route);
    if (isInCanvas) {
      sendBiEvent({
        name: AI_AGENTS_EVENTS.newAgentClick,
        props: { origin: 'agent_card', app: 'canvas' },
      });
      setIsAgentCreateActive(true);
      return;
    }

    if (!isLoadingFunnelMaps && funnelMaps.length === 0) {
      setIsCreateFunnelDialogOpened(true);
      return;
    }
    setIsProcessSelectionDialogOpened(true);
  }, [funnelMaps.length, isLoadingFunnelMaps, pathname, sendBiEvent, setIsAgentCreateActive]);

  const handleNewMonitoringAgent = useCallback(() => {
    setIsNewMonitoringAgentDialogOpened(true);
  }, []);

  const handleNewAgentClick = useCallback(
    async (type: AgentType) => {
      switch (type) {
        case AgentType.ProcessOptimization:
          handleNoOrgFirst(handleNewProcessAgent);
          return;
        case AgentType.MonitoringV2:
          handleNoOrgFirst(handleNewMonitoringAgent);
          return;
      }
    },
    [handleNoOrgFirst, handleNewProcessAgent, handleNewMonitoringAgent],
  );

  const closeConnectSfDialog = useCallback(() => {
    setIsConnectSfDialogOpen(false);
  }, []);

  return (
    <>
      <AgentsHomeContent
        closePanel={closePanel}
        onNewAgentClick={handleNewAgentClick}
        isPageView={isPageView}
      />

      {isProcessSelectionDialogOpened && (
        <ProcessSelectionDialog
          closeDialog={() => setIsProcessSelectionDialogOpened(false)}
          onSelect={navigateAndActivate}
        />
      )}

      <CreateMonitoringAgentDialogWrapper
        setIsNewMonitoringAgentDialogOpened={setIsNewMonitoringAgentDialogOpened}
        isNewMonitoringAgentDialogOpened={isNewMonitoringAgentDialogOpened}
        crmOrgId={crmOrgId ?? ''}
      />

      {isCreateFunnelDialogOpened && (
        <CreateFunnelDialog closeDialog={() => setIsCreateFunnelDialogOpened(false)} />
      )}
      {isConnectSfDialogOpen && (
        <ConnectToSfDialog
          onFinish={closeConnectSfDialog}
          closeDialog={closeConnectSfDialog}
          forceProduction={false}
        />
      )}
    </>
  );
};

const AgentsHomeWithArtifactContext = (props: AgentsHomeWithDialogsProps) => {
  const { forceCollapse } = useSidebarCollapse();
  return (
    <ArtifactProvider forceSidebarCollapse={forceCollapse}>
      <TableArtifactProvider>
        <AgentsHomeWithDialogs {...props} />
      </TableArtifactProvider>
    </ArtifactProvider>
  );
};

const AgentsHomeWithSidebarContext = (props: AgentsHomeWithDialogsProps) => {
  return (
    <SidebarCollapseProvider>
      <AgentsHomeWithArtifactContext {...props} />
    </SidebarCollapseProvider>
  );
};

export default AgentsHomeWithSidebarContext;
